import React, { useMemo, useState, useEffect } from 'react';
import { Row, Col, Button, Table, Card } from 'react-bootstrap';
import Loader from './Loader';
import { MultiSelectAsync, SelectOption } from './MultiSelectAsync';

type ReportDoc = {
  salesforce_report_document_id?: string;
  document_id?: string;
  file_name?: string | null;
  public_url?: string | null;
  report_type_standardized?: string | null;
  report_type?: string | null;
  report_year?: number | null;
  report_status?: string | null;
  upload_date?: string | null;
};

type Props = {
  loading: boolean;
  reportDocs: ReportDoc[];
};

const BUCKET_NAME = 'sf_documents_by_id';

function normalizeType(r: ReportDoc) {
  return (r.report_type_standardized ?? r.report_type ?? '').trim();
}

function prettyReportName(r: ReportDoc) {
  const type = r.report_type_standardized ?? r.report_type ?? 'Report';
  const year = r.report_year ? ` (${r.report_year})` : '';
  return `${type}${year}`;
}

function formatDate(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US');
}

function getDownloadUrl(r: ReportDoc) {
  if (r.public_url) return r.public_url;

  // Fallback until we verify missing public_url cases upstream
  if (r.file_name) return `https://storage.googleapis.com/${BUCKET_NAME}/${r.file_name}`;

  return null;
}

function statusClass(status?: string | null) {
  const s = (status ?? '').toLowerCase().trim().replace(/\s+/g, '-');
  return s ? `tu-status ${s}` : 'tu-status';
}

function uniq<T>(arr: T[]) {
  return Array.from(new Set(arr));
}

const EntityReportsOverview: React.FC<Props> = ({ loading, reportDocs }) => {
  const [showInfo, setShowInfo] = useState(false);

  // Multi-select state
  const [selectedYears, setSelectedYears] = useState<SelectOption[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<SelectOption[]>([]);

  // -----------------------
  // Build year options
  // -----------------------
  const yearOptions: SelectOption[] = useMemo(() => {
    const years = uniq(
      reportDocs
        .map((r) => r.report_year)
        .filter((y): y is number => typeof y === 'number')
    ).sort((a, b) => b - a);

    return years.map((y) => ({ value: String(y), label: String(y) }));
  }, [reportDocs]);

  const selectedYearValues = useMemo(() => new Set(selectedYears.map((y) => y.value)), [selectedYears]);
  const selectedTypeValues = useMemo(() => new Set(selectedTypes.map((t) => t.value)), [selectedTypes]);

  // -----------------------
  // Step A: filter by year first (drives type dropdown)
  // If no year selected => all years
  // -----------------------
  const yearFilteredDocs = useMemo(() => {
    if (selectedYears.length === 0) return reportDocs;
    return reportDocs.filter((r) => selectedYearValues.has(String(r.report_year ?? '')));
  }, [reportDocs, selectedYears, selectedYearValues]);

  // -----------------------
  // Step B: type options after year filter
  // -----------------------
  const typeOptions: SelectOption[] = useMemo(() => {
    const types = uniq(yearFilteredDocs.map(normalizeType).filter(Boolean)).sort((a, b) => a.localeCompare(b));
    return types.map((t) => ({ value: t, label: t }));
  }, [yearFilteredDocs]);

  // -----------------------
  // Step C: if selected types become invalid after year changes, drop them.
  // -----------------------
  useEffect(() => {
    if (selectedTypes.length === 0) return;
    const validSet = new Set(typeOptions.map((t) => t.value));
    const next = selectedTypes.filter((t) => validSet.has(t.value));
    if (next.length !== selectedTypes.length) setSelectedTypes(next);
  }, [typeOptions, selectedTypes]);

  // -----------------------
  // Final filter: year + type
  // If no type selected => all types
  // -----------------------
  const filteredDocs = useMemo(() => {
    let filtered = yearFilteredDocs;

    if (selectedTypes.length > 0) {
      filtered = filtered.filter((r) => selectedTypeValues.has(normalizeType(r)));
    }

    filtered = [...filtered].sort((a, b) => {
      const ad = a.upload_date ? new Date(a.upload_date).getTime() : 0;
      const bd = b.upload_date ? new Date(b.upload_date).getTime() : 0;
      return bd - ad;
    });

    return filtered;
  }, [yearFilteredDocs, selectedTypes, selectedTypeValues]);

  const clearFilters = () => {
    setSelectedYears([]);
    setSelectedTypes([]);
  };

  // -----------------------
  // Async loaders (supports searching)
  // -----------------------
  const loadYearOptions = async (inputValue: string) => {
    const q = inputValue.trim().toLowerCase();
    return yearOptions.filter((o) => o.label.toLowerCase().includes(q));
  };

  const loadTypeOptions = async (inputValue: string) => {
    const q = inputValue.trim().toLowerCase();
    return typeOptions.filter((o) => o.label.toLowerCase().includes(q));
  };

  if (loading) return <Loader />;

  const header = (
    <div className="tu-tabHeader">
      {/* <h1 className="text-white text-center m-0"> */}
        <div className="tu-tabHeader__titleRow">
          <h1 className="tu-tabHeader__title">
            Reports</h1>
            {/* <Button */}
            <button
              // className="text-white" // Added to match style consistencies of other tabs
              // variant="link" // Added to match style consistencies of other tabs
              type="button"
              className="tu-tabHeader__infoBtn"
              onClick={() => setShowInfo((v) => !v)}
              aria-expanded={showInfo}
              aria-label="About reports"
            >
              <i className="fas fa-info" />
            {/* </Button> */}
            </button>
          {/* </h1> */}
        </div>

      {showInfo && (
        <div className="tu-tabHeader__desc">
          Official reports submitted by this entity to the Office of the Utah State Auditor as required by statute. Use the filters below to refine results by year and report type. Download links open the original submitted document.
        </div>
      )}
    </div>
  );

  if (!reportDocs.length) {
    return (
      <div className="tu-reports tu-reports--tabulator">
        {header}
        {/* <div className="tu-reports__panel">
          <div className="tu-reports__empty">No reports found for this entity.</div>
        </div> */}
        <Card className="mb-4">
          <div className="alert alert-light shadow-sm">
              No data available
            </div>
        </Card>
      </div>
    );
  }

  return (
      <div className="tu-reports tu-reports--tabulator">
        {header}

        <Card className="mb-4">
          <div className="tu-reports__panel">
            {/* Filters */}
            <Row className="tu-reports__filters g-3 align-items-end">
              <Col xs={12} md={4}>
                <label className="tu-reports__label">Select year</label>
                <MultiSelectAsync
                  value={selectedYears}
                  onChange={(v) => setSelectedYears(v as SelectOption[])}
                  loadOptions={loadYearOptions}
                  defaultOptions={yearOptions}
                  closeMenuOnSelect={false}
                  options={yearOptions}
                />
              </Col>

              <Col xs={12} md={5}>
                <label className="tu-reports__label">Report type</label>
                <MultiSelectAsync
                  value={selectedTypes}
                  onChange={(v) => setSelectedTypes(v as SelectOption[])}
                  loadOptions={loadTypeOptions}
                  defaultOptions={typeOptions}
                  closeMenuOnSelect={false}
                  options={typeOptions}
                  isSearchable
                />
              </Col>

              <Col xs={12} md={3} className="d-grid">
                <Button variant="outline-secondary" className="tu-reports__clear" onClick={clearFilters}>
                  Clear filters
                </Button>
              </Col>
            </Row>

            {/* Count */}
            <div className="tu-reports__countRow">
              Showing <strong>{filteredDocs.length}</strong> of <strong>{reportDocs.length}</strong>
            </div>

            {/* Table */}
            <div className="tu-reports__tableWrap">
              {filteredDocs.length === 0 ? (
                <div className="tu-reports__empty">No reports match your filters.</div>
              ) : (
                <Table className="tu-reports__table" hover responsive>
                  <thead>
                    <tr>
                      <th>Report</th>
                      <th>Type</th>
                      <th className="tu-col-year">Year</th>
                      <th>Status</th>
                      <th className="tu-col-date">Uploaded</th>
                      <th className="tu-col-action">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocs.map((r) => {
                      const key = r.salesforce_report_document_id ?? r.document_id ?? r.file_name ?? `${Math.random()}`;
                      const url = getDownloadUrl(r);
                      const type = normalizeType(r);

                      return (
                        <tr key={key}>
                          <td className="tu-reports__primary">{prettyReportName(r)}</td>
                          <td>{type}</td>
                          <td className="tu-col-year">{r.report_year ?? ''}</td>
                          <td>
                            {r.report_status ? (
                              <span className={statusClass(r.report_status)}>{r.report_status}</span>
                            ) : (
                              <span className="tu-muted">—</span>
                            )}
                          </td>
                          <td className="tu-col-date">{formatDate(r.upload_date)}</td>
                          <td className="tu-col-action">
                            {url ? (
                              <a className="tu-link" href={url} target="_blank" rel="noreferrer">
                                Download
                              </a>
                            ) : (
                              <span className="tu-muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </Card>
      </div>
  );
};

export default EntityReportsOverview;